import * as React from 'react';
import { createRoute, Link, useParams } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Search, ListChecks } from 'lucide-react';
import type {
  StatementFileStatus,
  StatementRowStatus,
  StatementRowSummary,
  StatementSummary,
} from '@bits/shared/zod';
import { formatInr, paise } from '@bits/domain';
import { rootRoute } from './__root';
import { AppShell } from '@/components/AppShell';
import { api } from '@/lib/api';
import { errorMessage as toMessage } from '@/lib/errors';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { SortableTh } from '@/components/ui/sortable-th';
import { useSortedRows, type SortAccessors } from '@/lib/sort';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

export const ledgerStatementDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/ledger/statements/$fileId',
  component: LedgerStatementDetailPage,
});

type StatementRowSortKey = 'date' | 'description' | 'debit' | 'credit' | 'balance' | 'status';
const EMPTY_ROWS: StatementRowSummary[] = [];

/** A statement row with the account balance after it, as a ledger shows it. */
type LedgerRow = { row: StatementRowSummary; balancePaise: bigint | null };

const rowSortAccessors: SortAccessors<LedgerRow, StatementRowSortKey> = {
  date: ({ row }) => row.businessDate,
  description: ({ row }) => row.rawDescription,
  debit: ({ row }) => (row.direction === 'out' ? row.amountPaise : null),
  credit: ({ row }) => (row.direction === 'in' ? row.amountPaise : null),
  balance: ({ balancePaise }) => (balancePaise === null ? null : Number(balancePaise)),
  status: ({ row }) => ROW_STATUS_STYLE[row.status].label,
};

/**
 * Rows in the order the money moved. Dates alone cannot order a day's rows, so
 * the file's own order breaks ties: a newest-first file (its first line is the
 * latest date) is read backwards, anything else forwards.
 */
function chronological(rows: readonly StatementRowSummary[]): StatementRowSummary[] {
  const byLine = [...rows].sort((a, b) => a.lineNo - b.lineNo);
  const first = byLine[0];
  const last = byLine[byLine.length - 1];
  const newestFirst =
    first !== undefined && last !== undefined && first.businessDate > last.businessDate;
  const fileOrder = newestFirst ? byLine.reverse() : byLine;
  return fileOrder
    .map((row, i) => ({ row, i }))
    .sort((a, b) => a.row.businessDate.localeCompare(b.row.businessDate) || a.i - b.i)
    .map(({ row }) => row);
}

function withRunningBalance(
  rows: readonly StatementRowSummary[],
  openingPaise: number | null,
): LedgerRow[] {
  let balance = openingPaise === null ? null : BigInt(openingPaise);
  return rows.map((row) => {
    if (balance !== null) {
      balance += row.direction === 'in' ? BigInt(row.amountPaise) : -BigInt(row.amountPaise);
    }
    return { row, balancePaise: balance };
  });
}

const ROW_FILTERS: ReadonlyArray<{ value: StatementRowStatus | ''; label: string }> = [
  { value: '', label: 'All' },
  { value: 'pending', label: 'Pending' },
  { value: 'suggested', label: 'Suggested' },
  { value: 'confirmed', label: 'Confirmed' },
  { value: 'skipped', label: 'Skipped' },
];

const ROW_STATUS_STYLE: Record<StatementRowStatus, { label: string; dot: string; pill: string }> = {
  pending: {
    label: 'Pending',
    dot: 'bg-[hsl(var(--muted-foreground))]',
    pill: 'bg-[hsl(var(--muted))] text-[hsl(var(--muted-foreground))]',
  },
  suggested: {
    label: 'Suggested',
    dot: 'bg-[hsl(var(--brand))]',
    pill: 'bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))]',
  },
  confirmed: {
    label: 'Confirmed',
    dot: 'bg-white/80',
    pill: 'bg-[hsl(var(--brand))] text-[hsl(var(--brand-foreground))]',
  },
  skipped: {
    label: 'Skipped',
    dot: 'bg-[hsl(var(--destructive))]',
    pill: 'bg-[hsl(var(--destructive))]/10 text-[hsl(var(--destructive))]',
  },
};

const FILE_STATUS_STYLE: Record<StatementFileStatus, { label: string; dot: string; pill: string }> =
  {
    uploaded: {
      label: 'Uploaded',
      dot: 'bg-[hsl(var(--muted-foreground))]',
      pill: 'bg-[hsl(var(--muted))] text-[hsl(var(--muted-foreground))]',
    },
    parsed: {
      label: 'In review',
      dot: 'bg-[hsl(var(--brand))]',
      pill: 'bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))]',
    },
    in_review: {
      label: 'In review',
      dot: 'bg-[hsl(var(--brand))]',
      pill: 'bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))]',
    },
    posted: {
      label: 'Posted',
      dot: 'bg-white/80',
      pill: 'bg-[hsl(var(--brand))] text-[hsl(var(--brand-foreground))]',
    },
  };

function Pill({ style }: { style: { label: string; dot: string; pill: string } }): JSX.Element {
  return (
    <span
      className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-2.5 py-0.5 text-xs font-medium ${style.pill}`}
    >
      <span aria-hidden className={`size-1.5 rounded-full ${style.dot}`} />
      {style.label}
    </span>
  );
}

const DATE_FMT = new Intl.DateTimeFormat('en-IN', {
  day: '2-digit',
  month: 'short',
  year: 'numeric',
});

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  const [y, m, d] = iso.split('-').map(Number);
  if (!y || !m || !d) return iso;
  return DATE_FMT.format(new Date(Date.UTC(y, m - 1, d)));
}

function plural(n: number, one: string, many: string): string {
  return `${n} ${n === 1 ? one : many}`;
}

function signedTotal(rows: readonly StatementRowSummary[]): bigint {
  let total = 0n;
  for (const r of rows)
    total += r.direction === 'in' ? BigInt(r.amountPaise) : -BigInt(r.amountPaise);
  return total;
}

function sumWhere(rows: readonly StatementRowSummary[], direction: 'in' | 'out'): bigint {
  let total = 0n;
  for (const r of rows) if (r.direction === direction) total += BigInt(r.amountPaise);
  return total;
}

function StatTile({
  label,
  value,
  hint,
  tone = 'default',
}: {
  label: string;
  value: string;
  hint: string;
  tone?: 'default' | 'brand' | 'destructive';
}): JSX.Element {
  const valueTone =
    tone === 'brand'
      ? 'text-[hsl(var(--brand))]'
      : tone === 'destructive'
        ? 'text-[hsl(var(--destructive))]'
        : 'text-[hsl(var(--foreground))]';
  return (
    <div className="flex flex-col gap-1 rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] px-4 py-3.5">
      <span className="text-[11px] font-semibold uppercase tracking-[0.08em] text-[hsl(var(--muted-foreground))]">
        {label}
      </span>
      <span
        className={`font-display text-2xl font-semibold tabular-nums tracking-tight ${valueTone}`}
      >
        {value}
      </span>
      <span className="text-xs text-[hsl(var(--muted-foreground))]">{hint}</span>
    </div>
  );
}

function suggestionLabel(r: StatementRowSummary): string | null {
  if (r.suggestedCategoryName) return r.suggestedCategoryName;
  if (r.suggestedProjectId) return 'Project';
  if (r.suggestedCostCenterId) return 'Cost center';
  return null;
}

function LedgerStatementDetailPage(): JSX.Element {
  const { fileId } = useParams({ from: '/ledger/statements/$fileId' });
  const queryClient = useQueryClient();
  const [skipRowId, setSkipRowId] = React.useState<string | null>(null);
  const [skipReason, setSkipReason] = React.useState('');
  const [postDialogOpen, setPostDialogOpen] = React.useState(false);
  const [postError, setPostError] = React.useState<string | null>(null);
  const [overrideReason, setOverrideReason] = React.useState('');
  const [search, setSearch] = React.useState('');
  const [statusFilter, setStatusFilter] = React.useState<StatementRowStatus | ''>('');
  const [selected, setSelected] = React.useState<ReadonlySet<string>>(new Set());
  const [batch, setBatch] = React.useState<'confirm' | 'skip' | null>(null);
  const [batchError, setBatchError] = React.useState<string | null>(null);
  const [batchReason, setBatchReason] = React.useState('');
  const [batchCategoryId, setBatchCategoryId] = React.useState('');
  const [batchSink, setBatchSink] = React.useState<'overhead' | 'cost_center' | 'project'>(
    'overhead',
  );
  const [batchProjectId, setBatchProjectId] = React.useState('');
  const [batchCostCenterId, setBatchCostCenterId] = React.useState('');

  const summaryQuery = useQuery({
    queryKey: ['statement', fileId],
    queryFn: () => api.getStatement(fileId),
  });
  const rowsQuery = useQuery({
    queryKey: ['statement-rows', fileId],
    queryFn: () => api.listStatementRows(fileId, { page: 1, pageSize: 500 }),
  });
  const accountsQuery = useQuery({
    queryKey: ['accounts'],
    queryFn: () => api.listAccounts(),
  });
  const costCentersQuery = useQuery({
    queryKey: ['cost-centers'],
    queryFn: () => api.listCostCenters(),
  });
  const categoriesQuery = useQuery({
    queryKey: ['categories'],
    queryFn: () => api.listCategories(),
  });
  const projectsQuery = useQuery({
    queryKey: ['projects', 'active'],
    queryFn: () => api.listProjects({ status: 'active' }),
  });

  const refresh = async (): Promise<void> => {
    await queryClient.invalidateQueries({ queryKey: ['statement-rows', fileId] });
    await queryClient.invalidateQueries({ queryKey: ['statement', fileId] });
  };

  const confirmMutation = useMutation({
    mutationFn: ({
      rowId,
      body,
    }: {
      rowId: string;
      body: Parameters<typeof api.confirmStatementRow>[2];
    }) => api.confirmStatementRow(fileId, rowId, body),
    onSuccess: refresh,
  });

  const skipMutation = useMutation({
    mutationFn: ({ rowId, reason }: { rowId: string; reason: string }) =>
      api.skipStatementRow(fileId, rowId, { reason }),
    onSuccess: async () => {
      setSkipRowId(null);
      setSkipReason('');
      await refresh();
    },
  });

  const bulkMutation = useMutation({
    mutationFn: (body: Parameters<typeof api.bulkConfirmStatementRows>[1]) =>
      api.bulkConfirmStatementRows(fileId, body),
    onSuccess: async () => {
      closeBatch();
      await refresh();
    },
    onError: (err) => setBatchError(toMessage(err)),
  });

  const bulkSkipMutation = useMutation({
    mutationFn: (body: Parameters<typeof api.bulkSkipStatementRows>[1]) =>
      api.bulkSkipStatementRows(fileId, body),
    onSuccess: async () => {
      closeBatch();
      await refresh();
    },
    onError: (err) => setBatchError(toMessage(err)),
  });

  function closeBatch(): void {
    setBatch(null);
    setBatchError(null);
    setBatchReason('');
    setSelected(new Set());
  }

  const rerunMutation = useMutation({
    mutationFn: () => api.rerunSuggestions(fileId),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['statement-rows', fileId] });
    },
  });

  const postMutation = useMutation({
    mutationFn: (body: Parameters<typeof api.postStatement>[1]) => api.postStatement(fileId, body),
    onSuccess: async () => {
      setPostDialogOpen(false);
      setOverrideReason('');
      setPostError(null);
      await queryClient.invalidateQueries({ queryKey: ['statement', fileId] });
      await queryClient.invalidateQueries({ queryKey: ['statements'] });
    },
    onError: (err) => {
      setPostError(toMessage(err));
    },
  });

  const s = summaryQuery.data;
  const allRows = rowsQuery.data?.items ?? EMPTY_ROWS;
  const account = accountsQuery.data?.find((a) => a.id === s?.accountId) ?? null;

  const ledger = React.useMemo(
    () => withRunningBalance(chronological(allRows), s?.openingBalancePaise ?? null),
    [allRows, s?.openingBalancePaise],
  );
  const visible = React.useMemo(() => {
    const q = search.trim().toLowerCase();
    return ledger.filter(
      ({ row }) =>
        (!statusFilter || row.status === statusFilter) &&
        (!q || row.rawDescription.toLowerCase().includes(q)),
    );
  }, [ledger, search, statusFilter]);
  const { rows: ledgerRows, ...sorter } = useSortedRows(visible, rowSortAccessors);
  const rows = ledgerRows.map(({ row }) => row);
  const showBalance = s?.openingBalancePaise !== null && s?.openingBalancePaise !== undefined;

  const suggestedIds = allRows.filter((r) => r.status === 'suggested').map((r) => r.id);
  const reviewed = allRows.filter((r) => r.status === 'confirmed' || r.status === 'skipped').length;
  const toReview = allRows.length - reviewed;
  const allDoneOrSkipped = allRows.length > 0 && toReview === 0;
  const tie = balanceTie(s, allRows);

  const confirmRow = (r: StatementRowSummary): void =>
    confirmMutation.mutate({
      rowId: r.id,
      body: {
        projectId: r.suggestedProjectId ?? undefined,
        costCenterId: r.suggestedProjectId
          ? undefined
          : (r.suggestedCostCenterId ??
            costCentersQuery.data?.find((c) => c.isDefaultOverhead)?.id),
        categoryId: r.suggestedCategoryId ?? undefined,
      },
    });

  const filtered = Boolean(statusFilter || search.trim());
  const clearFilters = (): void => {
    setStatusFilter('');
    setSearch('');
  };

  const isOpenRow = (r: StatementRowSummary): boolean =>
    r.status === 'pending' || r.status === 'suggested';
  const openIds = React.useMemo(
    () => new Set(allRows.filter(isOpenRow).map((r) => r.id)),
    [allRows],
  );
  // A row confirmed or skipped elsewhere drops out of the selection on refresh.
  const selectedIds = React.useMemo(
    () => [...selected].filter((id) => openIds.has(id)),
    [selected, openIds],
  );
  const visibleOpen = rows.filter(isOpenRow);
  const allVisibleSelected = visibleOpen.length > 0 && visibleOpen.every((r) => selected.has(r.id));
  const someVisibleSelected = visibleOpen.some((r) => selected.has(r.id));
  const toggleRow = (id: string): void =>
    setSelected((cur) => {
      const next = new Set(cur);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const toggleVisible = (): void =>
    setSelected((cur) => {
      const next = new Set(cur);
      if (allVisibleSelected) for (const r of visibleOpen) next.delete(r.id);
      else for (const r of visibleOpen) next.add(r.id);
      return next;
    });
  const selectedRows = allRows.filter((r) => selected.has(r.id) && openIds.has(r.id));
  const selectedDirections = new Set(selectedRows.map((r) => r.direction));
  const overheadId = costCentersQuery.data?.find((c) => c.isDefaultOverhead)?.id;
  const categories = (categoriesQuery.data ?? []).filter((c) => c.active);
  const activeCostCenters = (costCentersQuery.data ?? []).filter((c) => c.active);
  const projects = projectsQuery.data ?? [];

  const openBatch = (kind: 'confirm' | 'skip'): void => {
    setBatchError(null);
    setBatchCategoryId('');
    setBatchSink('overhead');
    setBatchProjectId('');
    setBatchCostCenterId('');
    setBatch(kind);
  };
  const batchMappingReady =
    batchSink === 'overhead'
      ? Boolean(overheadId)
      : batchSink === 'project'
        ? Boolean(batchProjectId)
        : Boolean(batchCostCenterId);
  const submitBatchConfirm = (): void =>
    bulkMutation.mutate({
      rowIds: selectedIds,
      mapping: {
        ...(batchCategoryId ? { categoryId: batchCategoryId } : {}),
        ...(batchSink === 'project'
          ? { projectId: batchProjectId }
          : { costCenterId: batchSink === 'overhead' ? overheadId : batchCostCenterId }),
      },
    });

  if (summaryQuery.isPending || !s) {
    return (
      <AppShell>
        <p className="text-sm text-[hsl(var(--muted-foreground))]">Loading…</p>
      </AppShell>
    );
  }

  return (
    <AppShell>
      <div className="flex flex-col gap-4">
        <Link
          to="/ledger/statements"
          className="inline-flex w-fit items-center gap-1.5 text-sm text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--brand))]"
        >
          <ArrowLeft aria-hidden className="size-4" />
          Statements
        </Link>
        <div className="flex flex-wrap items-start justify-between gap-4 lg:flex-nowrap">
          <div className="flex min-w-0 flex-col gap-2">
            <div className="flex flex-wrap items-center gap-3">
              <h1 className="font-display text-4xl font-semibold tracking-tight md:text-5xl">
                {account?.name ?? 'Statement'}
              </h1>
              <Pill style={FILE_STATUS_STYLE[s.status]} />
            </div>
            <p className="text-base text-[hsl(var(--muted-foreground))]">
              {formatDate(s.fromDate)} to {formatDate(s.toDate)}
              {account ? ` · ${account.numberMasked}` : ''}
              {' · '}
              <span className="break-all" title={`sha256 ${s.sha256}`}>
                {s.filename}
              </span>
            </p>
          </div>
          <div className="flex shrink-0 flex-wrap items-center gap-2">
            <Button
              variant="outline"
              onClick={() => rerunMutation.mutate()}
              disabled={rerunMutation.isPending || s.status === 'posted'}
            >
              Re-run suggestions
            </Button>
            <Button
              variant="outline"
              onClick={() => bulkMutation.mutate({ rowIds: suggestedIds })}
              disabled={suggestedIds.length === 0 || bulkMutation.isPending}
            >
              Confirm all suggested{suggestedIds.length > 0 ? ` (${suggestedIds.length})` : ''}
            </Button>
            <Button
              onClick={() => setPostDialogOpen(true)}
              disabled={!allDoneOrSkipped || s.status === 'posted'}
              title={
                s.status === 'posted'
                  ? 'Already posted'
                  : allDoneOrSkipped
                    ? undefined
                    : `${plural(toReview, 'row', 'rows')} still to review`
              }
            >
              {s.status === 'posted' ? 'Posted' : 'Post to ledger'}
            </Button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 xl:grid-cols-4">
        <StatTile
          label="Opening balance"
          value={s.openingBalancePaise !== null ? formatInr(paise(s.openingBalancePaise)) : '—'}
          hint={s.fromDate ? `As of ${formatDate(s.fromDate)}` : 'Not stated in the file'}
        />
        <StatTile
          label="Closing balance"
          value={s.closingBalancePaise !== null ? formatInr(paise(s.closingBalancePaise)) : '—'}
          hint={tie.hint}
          tone={tie.tone}
        />
        <StatTile
          label="Total credits"
          value={formatInr(paise(Number(sumWhere(allRows, 'in'))))}
          hint={plural(allRows.filter((r) => r.direction === 'in').length, 'credit', 'credits')}
          tone="brand"
        />
        <StatTile
          label="Total debits"
          value={formatInr(paise(Number(sumWhere(allRows, 'out'))))}
          hint={plural(allRows.filter((r) => r.direction === 'out').length, 'debit', 'debits')}
        />
      </div>

      <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center">
        <div className="relative w-full sm:max-w-xs sm:flex-1">
          <Search
            aria-hidden
            className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-[hsl(var(--muted-foreground))]"
          />
          <Input
            className="pl-9"
            placeholder="Search description…"
            value={search}
            onChange={(e) => setSearch(e.currentTarget.value)}
          />
        </div>
        <div className="-mx-1 overflow-x-auto px-1">
          <div className="flex w-max rounded-xl border border-[hsl(var(--border))] p-0.5">
            {ROW_FILTERS.map((f) => {
              const count = f.value ? s.rowCounts[f.value] : allRows.length;
              return (
                <button
                  key={f.value}
                  type="button"
                  onClick={() => setStatusFilter(f.value)}
                  className={
                    'whitespace-nowrap rounded-lg px-3 py-1.5 text-sm transition-colors ' +
                    (statusFilter === f.value
                      ? 'bg-[hsl(var(--brand))] text-white'
                      : 'text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))]')
                  }
                >
                  {f.label}
                  <span className="ml-1.5 tabular-nums opacity-70">{count}</span>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      <Card className="overflow-hidden">
        <CardHeader className="flex-row items-end justify-between gap-4 space-y-0">
          <div className="flex flex-col gap-1">
            <CardTitle>
              {statusFilter ? ROW_STATUS_STYLE[statusFilter].label : 'All'} rows
            </CardTitle>
            <CardDescription>
              {rows.length === allRows.length
                ? `${plural(allRows.length, 'row', 'rows')} · ${reviewed} reviewed`
                : `Showing ${rows.length} of ${allRows.length}`}
            </CardDescription>
          </div>
          {selectedIds.length > 0 ? (
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm tabular-nums text-[hsl(var(--muted-foreground))]">
                {plural(selectedIds.length, 'row', 'rows')} selected
              </span>
              <Button
                size="sm"
                onClick={() => openBatch('confirm')}
                disabled={s.status === 'posted'}
              >
                Confirm selected
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => openBatch('skip')}
                disabled={s.status === 'posted'}
              >
                Skip selected
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setSelected(new Set())}>
                Clear
              </Button>
            </div>
          ) : rowsQuery.isFetching ? (
            <span className="text-xs text-[hsl(var(--muted-foreground))]">Refreshing…</span>
          ) : null}
        </CardHeader>
        <CardContent className="p-0">
          {rowsQuery.isPending ? (
            <p className="px-6 py-10 text-center text-sm text-[hsl(var(--muted-foreground))]">
              Loading…
            </p>
          ) : rows.length === 0 ? (
            <div className="flex flex-col items-center gap-3 px-6 py-14 text-center">
              <div className="flex size-12 items-center justify-center rounded-full bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))]">
                <ListChecks aria-hidden className="size-5" />
              </div>
              <p className="text-sm text-[hsl(var(--muted-foreground))]">
                {filtered ? 'No rows match these filters.' : 'This statement has no rows.'}
              </p>
              {filtered ? (
                <Button variant="outline" size="sm" onClick={clearFilters}>
                  Clear filters
                </Button>
              ) : null}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[920px] border-collapse text-[13px] leading-tight">
                <thead>
                  <tr className="border-y border-[hsl(var(--border))] bg-[hsl(var(--muted))] text-left text-[11px] font-semibold uppercase tracking-[0.08em] text-[hsl(var(--muted-foreground))]">
                    <th className="w-14 py-2 pl-5 pr-4 align-middle">
                      <Checkbox
                        aria-label="Select every open row shown"
                        checked={allVisibleSelected}
                        indeterminate={!allVisibleSelected && someVisibleSelected}
                        disabled={visibleOpen.length === 0 || s.status === 'posted'}
                        onChange={toggleVisible}
                      />
                    </th>
                    <SortableTh sortKey="date" controller={sorter} className="w-28 py-2 pr-3">
                      Date
                    </SortableTh>
                    <SortableTh sortKey="description" controller={sorter} className="py-2 pr-3">
                      Particulars
                    </SortableTh>
                    <SortableTh
                      sortKey="debit"
                      controller={sorter}
                      align="right"
                      className="w-32 py-2 pr-3"
                    >
                      Debit
                    </SortableTh>
                    <SortableTh
                      sortKey="credit"
                      controller={sorter}
                      align="right"
                      className="w-32 py-2 pr-3"
                    >
                      Credit
                    </SortableTh>
                    {showBalance ? (
                      <SortableTh
                        sortKey="balance"
                        controller={sorter}
                        align="right"
                        className="w-36 py-2 pr-3"
                      >
                        Balance
                      </SortableTh>
                    ) : null}
                    <SortableTh sortKey="status" controller={sorter} className="w-28 py-2 pr-3">
                      Status
                    </SortableTh>
                    <th className="w-36 py-2 pr-5 text-right" aria-label="Actions" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-[hsl(var(--border))]">
                  {ledgerRows.map(({ row: r, balancePaise }) => {
                    const suggestion = suggestionLabel(r);
                    const open = r.status === 'pending' || r.status === 'suggested';
                    const isSelected = selected.has(r.id);
                    return (
                      <tr
                        key={r.id}
                        className={`transition-colors ${
                          isSelected
                            ? 'bg-[hsl(var(--accent))]/60'
                            : 'odd:bg-[hsl(var(--muted))]/35 hover:bg-[hsl(var(--accent))]/40'
                        }`}
                      >
                        <td className="w-14 py-1.5 pl-5 pr-4 align-middle">
                          {open ? (
                            <Checkbox
                              aria-label={`Select row dated ${formatDate(r.businessDate)}`}
                              checked={isSelected}
                              disabled={s.status === 'posted'}
                              onChange={() => toggleRow(r.id)}
                            />
                          ) : null}
                        </td>
                        <td className="whitespace-nowrap py-1.5 pr-3 align-middle tabular-nums text-[hsl(var(--muted-foreground))]">
                          {formatDate(r.businessDate)}
                        </td>
                        <td className="py-1.5 pr-3 align-middle">
                          <div className="flex min-w-0 flex-col">
                            <span className="max-w-[460px] truncate" title={r.rawDescription}>
                              {r.rawDescription}
                            </span>
                            {suggestion ? (
                              <span className="text-[11px] text-[hsl(var(--muted-foreground))]">
                                Suggested: {suggestion}
                              </span>
                            ) : r.matchedTransactionId ? (
                              <span className="text-[11px] text-[hsl(var(--muted-foreground))]">
                                Posted to the ledger
                              </span>
                            ) : null}
                          </div>
                        </td>
                        <td className="whitespace-nowrap py-1.5 pr-3 text-right align-middle tabular-nums">
                          {r.direction === 'out' ? formatInr(paise(r.amountPaise)) : ''}
                        </td>
                        <td className="whitespace-nowrap py-1.5 pr-3 text-right align-middle tabular-nums text-[hsl(var(--brand))]">
                          {r.direction === 'in' ? formatInr(paise(r.amountPaise)) : ''}
                        </td>
                        {showBalance ? (
                          <td
                            className={`whitespace-nowrap py-1.5 pr-3 text-right align-middle font-medium tabular-nums ${
                              balancePaise !== null && balancePaise < 0n
                                ? 'text-[hsl(var(--destructive))]'
                                : ''
                            }`}
                          >
                            {balancePaise === null ? '' : formatInr(paise(Number(balancePaise)))}
                          </td>
                        ) : null}
                        <td className="py-1.5 pr-3 align-middle">
                          <Pill style={ROW_STATUS_STYLE[r.status]} />
                        </td>
                        <td className="py-1.5 pr-5 text-right align-middle">
                          {open ? (
                            <div className="flex justify-end gap-1">
                              <Button
                                size="sm"
                                variant="outline"
                                className="h-7 px-2.5 text-xs"
                                onClick={() => confirmRow(r)}
                                disabled={confirmMutation.isPending || s.status === 'posted'}
                              >
                                Confirm
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                className="h-7 px-2.5 text-xs"
                                onClick={() => setSkipRowId(r.id)}
                                disabled={skipMutation.isPending || s.status === 'posted'}
                              >
                                Skip
                              </Button>
                            </div>
                          ) : null}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr className="border-t-2 border-[hsl(var(--border))] bg-[hsl(var(--muted))] text-[12px] font-semibold">
                    <td className="py-2 pl-5 pr-4" />
                    <td className="py-2 pr-3" colSpan={2}>
                      {rows.length === allRows.length
                        ? 'Totals'
                        : `Totals for the ${plural(rows.length, 'row', 'rows')} shown`}
                    </td>
                    <td className="whitespace-nowrap py-2 pr-3 text-right tabular-nums">
                      {formatInr(paise(Number(sumWhere(rows, 'out'))))}
                    </td>
                    <td className="whitespace-nowrap py-2 pr-3 text-right tabular-nums text-[hsl(var(--brand))]">
                      {formatInr(paise(Number(sumWhere(rows, 'in'))))}
                    </td>
                    {showBalance ? (
                      <td className="whitespace-nowrap py-2 pr-3 text-right tabular-nums">
                        {rows.length === allRows.length && s.closingBalancePaise !== null
                          ? formatInr(paise(s.closingBalancePaise))
                          : ''}
                      </td>
                    ) : null}
                    <td className="py-2 pr-3" colSpan={2} />
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={skipRowId !== null} onOpenChange={(open) => !open && setSkipRowId(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Skip row</DialogTitle>
            <DialogDescription>
              Record why this row is not being posted. Skipped rows still count toward the balance
              check.
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="skipReason">Reason</Label>
            <Input
              id="skipReason"
              value={skipReason}
              onChange={(e) => setSkipReason(e.target.value)}
              placeholder="Already recorded manually, internal transfer…"
            />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setSkipRowId(null)}>
              Cancel
            </Button>
            <Button
              onClick={() =>
                skipRowId && skipMutation.mutate({ rowId: skipRowId, reason: skipReason })
              }
              disabled={!skipReason.trim() || skipMutation.isPending}
            >
              Skip row
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={batch === 'confirm'} onOpenChange={(open) => !open && closeBatch()}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm {plural(selectedIds.length, 'row', 'rows')}</DialogTitle>
            <DialogDescription>
              Every selected row is booked to the ledger with this mapping. Each row still becomes
              its own transaction.
            </DialogDescription>
          </DialogHeader>
          {selectedDirections.size > 1 ? (
            <Alert>
              <AlertTitle>Mixed credits and debits</AlertTitle>
              <AlertDescription>
                The selection has money in and money out. One category rarely fits both, so consider
                confirming them in two batches.
              </AlertDescription>
            </Alert>
          ) : null}
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="batchCategory">Category</Label>
            <Select
              id="batchCategory"
              value={batchCategoryId}
              onChange={(e) => setBatchCategoryId(e.currentTarget.value)}
            >
              <option value="">No category</option>
              {categories.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name} · {c.kind}
                </option>
              ))}
            </Select>
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="batchSink">Charge to</Label>
            <Select
              id="batchSink"
              value={batchSink}
              onChange={(e) =>
                setBatchSink(e.currentTarget.value as 'overhead' | 'cost_center' | 'project')
              }
            >
              <option value="overhead">Overhead (default)</option>
              <option value="cost_center">A cost center</option>
              <option value="project">A project</option>
            </Select>
          </div>
          {batchSink === 'cost_center' ? (
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="batchCostCenter">Cost center</Label>
              <Select
                id="batchCostCenter"
                value={batchCostCenterId}
                onChange={(e) => setBatchCostCenterId(e.currentTarget.value)}
              >
                <option value="">Choose…</option>
                {activeCostCenters.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </Select>
            </div>
          ) : null}
          {batchSink === 'project' ? (
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="batchProject">Project</Label>
              <Select
                id="batchProject"
                value={batchProjectId}
                onChange={(e) => setBatchProjectId(e.currentTarget.value)}
              >
                <option value="">Choose…</option>
                {projects.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.code} · {p.name}
                  </option>
                ))}
              </Select>
            </div>
          ) : null}
          {batchError ? (
            <Alert variant="destructive">
              <AlertTitle>Nothing was confirmed</AlertTitle>
              <AlertDescription>{batchError}</AlertDescription>
            </Alert>
          ) : null}
          <DialogFooter>
            <Button variant="ghost" onClick={closeBatch}>
              Cancel
            </Button>
            <Button
              onClick={submitBatchConfirm}
              disabled={!batchMappingReady || selectedIds.length === 0 || bulkMutation.isPending}
            >
              {bulkMutation.isPending
                ? 'Confirming…'
                : `Confirm ${plural(selectedIds.length, 'row', 'rows')}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={batch === 'skip'} onOpenChange={(open) => !open && closeBatch()}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Skip {plural(selectedIds.length, 'row', 'rows')}</DialogTitle>
            <DialogDescription>
              One reason is recorded against every selected row. Skipped rows still count toward the
              balance check.
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="batchReason">Reason</Label>
            <Input
              id="batchReason"
              value={batchReason}
              onChange={(e) => setBatchReason(e.target.value)}
              placeholder="Already recorded manually, internal transfer…"
            />
          </div>
          {batchError ? (
            <Alert variant="destructive">
              <AlertTitle>Nothing was skipped</AlertTitle>
              <AlertDescription>{batchError}</AlertDescription>
            </Alert>
          ) : null}
          <DialogFooter>
            <Button variant="ghost" onClick={closeBatch}>
              Cancel
            </Button>
            <Button
              onClick={() => bulkSkipMutation.mutate({ rowIds: selectedIds, reason: batchReason })}
              disabled={
                !batchReason.trim() || selectedIds.length === 0 || bulkSkipMutation.isPending
              }
            >
              {bulkSkipMutation.isPending
                ? 'Skipping…'
                : `Skip ${plural(selectedIds.length, 'row', 'rows')}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={postDialogOpen} onOpenChange={setPostDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Post statement to the ledger</DialogTitle>
            <DialogDescription>
              Checks that the opening balance plus every row equals the closing balance, then posts
              the confirmed rows. Finance can override a failed check with a reason.
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="override">Override reason</Label>
            <Input
              id="override"
              value={overrideReason}
              onChange={(e) => setOverrideReason(e.target.value)}
              placeholder="Leave blank unless the balance check fails"
            />
          </div>
          {postError && (
            <Alert variant="destructive">
              <AlertTitle>Post failed</AlertTitle>
              <AlertDescription>{postError}</AlertDescription>
            </Alert>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setPostDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={() => postMutation.mutate(overrideReason ? { overrideReason } : {})}
              disabled={postMutation.isPending}
            >
              Post
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AppShell>
  );
}

function balanceTie(
  s: StatementSummary | undefined,
  rows: readonly StatementRowSummary[],
): { hint: string; tone: 'default' | 'brand' | 'destructive' } {
  if (!s || s.openingBalancePaise === null || s.closingBalancePaise === null) {
    return { hint: 'Not stated in the file', tone: 'default' };
  }
  if (rows.length === 0)
    return { hint: s.toDate ? `As of ${formatDate(s.toDate)}` : '', tone: 'default' };
  const delta = BigInt(s.closingBalancePaise) - (BigInt(s.openingBalancePaise) + signedTotal(rows));
  if (delta === 0n) return { hint: 'Ties with opening plus every row', tone: 'default' };
  const off = formatInr(paise(Number(delta < 0n ? -delta : delta)));
  return { hint: `Off by ${off} against the rows`, tone: 'destructive' };
}
